// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import { LicenseOutput, PermissionsEnum } from 'thehive-sdk';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { useMemo } from 'react';
import { computeEventList } from '@Organisms/License/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import LicenseManage from '@Organisms/License/LicenseManage/LicenseManage';
import { THLayout } from '@Molecules/THLayout';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { LicenseContent } from '@Organisms/License/LicenseContent/LicenseContent';
import { StatusContext } from '@Context/StatusProvider';
import { ICONS } from '@Constants/iconsApp';

import './LicenseDetails.scss';

const LicenseDetails = (): ReactElement => {
    const i = useLocalMessage();

    const { quotasRaw, validLicense } = useContext(StatusContext);

    const { data: licenseDetails, refetch: licenseRefetch } = useGenericQuery<LicenseOutput>({
        fetchCallback: () => client.LicenseController.get<LicenseOutput>(),
        key: ['license'],
        manual: true,
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.LicenseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    licenseRefetch();
                },
            }),
        [i, licenseRefetch],
    );

    const { error, fallback, license, notFound } = licenseDetails || {};

    return (
        <THLayout>
            <THLayout.Content>
                <div className="license-details">
                    <div className="license-details__title">
                        {i('license.details.title')}
                        <ColorLabel
                            color={!license ? 'volcano' : validLicense ? 'green' : 'amber'}
                            label={
                                license
                                    ? license.plan
                                    : i(error ? 'general.error' : notFound ? 'general.no-license' : 'general.not-found')
                            }
                            className="license-details__title__status"
                        />
                    </div>

                    <div className="license-details__manage">
                        <LicenseManage
                            onFinish={(license) => eventList.onSet(license)}
                            triggerElem={
                                <THButton type="primary">
                                    {i(error || notFound ? 'license.details.activate' : 'license.details.update')}
                                </THButton>
                            }
                        />
                    </div>

                    {error && <div className="license-details__error">{error}</div>}

                    {fallback && !notFound && (
                        <div className="license-details__fallback">{i('license.details.fallback')}</div>
                    )}

                    <div className="license-details__content">
                        {(license || fallback) && (
                            <LicenseContent
                                license={license || fallback}
                                quotas={quotasRaw}
                                notFound={!license && notFound}
                            />
                        )}
                    </div>
                </div>
            </THLayout.Content>
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.license');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.license',
        icon: <ICONS.LicenseIcon />,
    },
    breadcrumb: { label: 'breadcrumb.global.license' },
    content: <LicenseDetails />,
    permissionNeeded: [PermissionsEnum.MANAGE_PLATFORM],
    space: ['admin'],
    exact: true,
};
